/**
 * StepBarChart
 * -----------------------------------------------------------------------------
 * A self-contained, responsive, grouped bar chart for step/answer tracker data.
 *
 * - X axis: one group per calendar day, labelled M/d
 * - Y axis: score (0 .. highest option value found in the data)
 * - One bar per step, per day. Missing answers leave the slot empty.
 * - Bars fill the visible width when they fit; otherwise they keep a readable
 *   minimum width and the plot scrolls horizontally (starts at the latest day).
 * - Every step gets both a distinct color AND a distinct SVG pattern, so the
 *   chart stays readable without color vision.
 *
 * Compatible with React 16.8+ (hooks) and TypeScript 4.9.4. No dependencies.
 */

import * as React from 'react';

/* ---------------------------------------------------------------- types --- */

export interface StepAnswerOption {
  label: string;
  value: number;
  _id?: string;
}

export interface Step {
  type?: string;
  content: string;
  shortName: string;
  nextButtonText?: string;
  answers?: StepAnswerOption[];
  hasCorrectAnswer?: boolean;
  correctAnswer?: number;
  showChart?: boolean;
  chartType?: string;
  chartFrequency?: string;
  _id?: string;
}

export interface StepAnswer {
  answer: number;
  date: string;
}

export interface StepTrend {
  trend: string;
  slope: number;
}

export interface BarDatum {
  step: Step;
  answers: StepAnswer[];
  trends?: { overall?: StepTrend; weekly?: StepTrend };
  trendDescription?: string;
}

export interface StepBarChartProps {
  /** 1 to 10 distinct steps. Extra steps beyond 10 are ignored. */
  barDataArray: BarDatum[];
  /** Total chart height in px, legend excluded. Default 280. */
  height?: number;
  /** Accessible description of the whole chart. */
  ariaLabel?: string;
  className?: string;
}

/* ------------------------------------------------------------ constants --- */

const MAX_STEPS = 10;
const AXIS_WIDTH = 46;
const PAD_TOP = 14;
const PAD_BOTTOM = 30;
const GROUP_PAD = 8;
const BAR_GAP = 3;
const MIN_BAR = 6;
const MAX_BAR = 28;
const DEFAULT_HEIGHT = 280;

type PatternKind =
  | 'diag'
  | 'dots'
  | 'horiz'
  | 'grid'
  | 'diagBack'
  | 'checker'
  | 'vert'
  | 'rings'
  | 'cross'
  | 'zigzag';

const COLORS: string[] = [
  '#2563eb',
  '#db2777',
  '#059669',
  '#d97706',
  '#7c3aed',
  '#0891b2',
  '#dc2626',
  '#4d7c0f',
  '#c026d3',
  '#475569'
];

const PATTERNS: PatternKind[] = [
  'diag',
  'dots',
  'horiz',
  'grid',
  'diagBack',
  'checker',
  'vert',
  'rings',
  'cross',
  'zigzag'
];

/**
 * Score axis labels. 0 is deliberately blank — the baseline needs no face.
 * Any tick without an entry here falls back to its plain number, so the chart
 * still reads correctly if a question ever uses a scale wider than 1-4.
 */
const EMOJI_SCALE: { [value: number]: string } = {
  1: '\uD83D\uDE41', // slightly frowning face
  2: '\uD83D\uDE10', // neutral face
  3: '\uD83D\uDE42', // slightly smiling face
  4: '\uD83E\uDD29' // star-struck
};

const EMOJI_ALT: { [value: number]: string } = {
  1: 'Poor',
  2: 'Neutral',
  3: 'Good',
  4: 'Excellent'
};

/* ------------------------------------------------------------- patterns --- */

function patternMarks(kind: PatternKind): JSX.Element {
  const ink = 'rgba(255,255,255,0.92)';
  switch (kind) {
    case 'diag':
      return (
        <path
          d="M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4"
          fill="none"
          stroke={ink}
          strokeWidth={1.6}
        />
      );
    case 'diagBack':
      return (
        <path
          d="M-2,6 l4,4 M0,0 l8,8 M6,-2 l4,4"
          fill="none"
          stroke={ink}
          strokeWidth={1.6}
        />
      );
    case 'vert':
      return <path d="M2,0 v8 M6,0 v8" fill="none" stroke={ink} strokeWidth={1.6} />;
    case 'horiz':
      return <path d="M0,2 h8 M0,6 h8" fill="none" stroke={ink} strokeWidth={1.6} />;
    case 'dots':
      return (
        <g fill={ink}>
          <circle cx={2} cy={2} r={1.4} />
          <circle cx={6} cy={6} r={1.4} />
        </g>
      );
    case 'grid':
      return <path d="M0,4 h8 M4,0 v8" fill="none" stroke={ink} strokeWidth={1.2} />;
    case 'cross':
      return (
        <path
          d="M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4 M-2,6 l4,4 M0,0 l8,8 M6,-2 l4,4"
          fill="none"
          stroke={ink}
          strokeWidth={1.1}
        />
      );
    case 'checker':
      return (
        <g fill={ink}>
          <rect x={0} y={0} width={4} height={4} />
          <rect x={4} y={4} width={4} height={4} />
        </g>
      );
    case 'rings':
      return <circle cx={4} cy={4} r={2.4} fill="none" stroke={ink} strokeWidth={1.3} />;
    case 'zigzag':
    default:
      return (
        <path
          d="M0,6 l2,-4 l2,4 l2,-4 l2,4"
          fill="none"
          stroke={ink}
          strokeWidth={1.4}
        />
      );
  }
}

interface PatternDefsProps {
  idPrefix: string;
  count: number;
}

function PatternDefs(props: PatternDefsProps) {
  const items: JSX.Element[] = [];
  for (let i = 0; i < props.count; i++) {
    const color = COLORS[i % COLORS.length];
    items.push(
      <pattern
        key={i}
        id={props.idPrefix + '-' + i}
        patternUnits="userSpaceOnUse"
        width={8}
        height={8}
      >
        <rect x={0} y={0} width={8} height={8} fill={color} />
        {patternMarks(PATTERNS[i % PATTERNS.length])}
      </pattern>
    );
  }
  return <defs>{items}</defs>;
}

/* ---------------------------------------------------------------- utils --- */

interface DayGroup {
  key: string;
  ts: number;
  label: string;
  full: string;
  values: { [stepIndex: number]: number };
}

function two(n: number): string {
  return n < 10 ? '0' + n : '' + n;
}

function makeTicks(max: number): number[] {
  const step = Math.max(1, Math.ceil(max / 5));
  const ticks: number[] = [];
  for (let v = 0; v <= max; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] !== max) ticks.push(max);
  return ticks;
}

function labelForValue(step: Step, value: number): string {
  const opts = (step && step.answers) || [];
  for (let i = 0; i < opts.length; i++) {
    if (opts[i].value === value) return opts[i].label;
  }
  return String(value);
}

const useIsoLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

/* ------------------------------------------------------------------ css --- */

/* The axis line is drawn at x = AXIS_WIDTH - 0.5, and each swatch rect starts at
   x = 0.5 inside its own svg, so indenting the legend by AXIS_WIDTH - 1 lines the
   left edge of the first swatch up with the vertical axis. */
const LEGEND_INDENT = AXIS_WIDTH - 1;

const CSS = `
.sbc-root{width:100%;color:#334155;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased;}
.sbc-plot{display:flex;align-items:stretch;width:100%;}
.sbc-axis{flex:0 0 auto;display:block;}
.sbc-scroll{flex:1 1 auto;min-width:0;overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch;}
.sbc-scroll > svg{display:block;}
.sbc-scroll::-webkit-scrollbar{height:8px;}
.sbc-scroll::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:4px;}
.sbc-scroll::-webkit-scrollbar-track{background:transparent;}
.sbc-tick{fill:#64748b;font-size:11px;}
.sbc-tick-emoji{font-size:16px;dominant-baseline:auto;}
.sbc-daylabel{fill:#475569;font-size:11px;}
.sbc-grid{stroke:#e2e8f0;stroke-width:1;shape-rendering:crispEdges;}
.sbc-axisline{stroke:#94a3b8;stroke-width:1;shape-rendering:crispEdges;}
.sbc-band{fill:#0f172a;opacity:0.03;}
.sbc-bar{transition:opacity .12s ease-out;}
.sbc-bar:hover{opacity:.82;}
.sbc-legend{display:flex;flex-wrap:wrap;list-style:none;margin:10px 0 0;padding:0 0 0 ${LEGEND_INDENT}px;}
.sbc-legend-item{display:inline-flex;align-items:center;margin:0 14px 6px 0;font-size:12px;line-height:1.3;color:#334155;}
.sbc-swatch{flex:0 0 auto;margin-right:6px;display:block;border-radius:2px;}
.sbc-empty{padding:24px 8px;font-size:13px;color:#64748b;text-align:center;}
@media (prefers-reduced-motion: reduce){.sbc-bar{transition:none;}}
`;

/* ------------------------------------------------------------ component --- */

export default function StepBarChart(props: StepBarChartProps) {
  const height = props.height || DEFAULT_HEIGHT;

  const data: BarDatum[] = React.useMemo(
    function () {
      const raw = props.barDataArray || [];
      return raw.slice(0, MAX_STEPS);
    },
    [props.barDataArray]
  );

  const uidRef = React.useRef<string>(
    'sbc' + Math.random().toString(36).substring(2, 9)
  );
  const uid = uidRef.current;

  /* ---- measure available width -------------------------------------- */
  const plotRef = React.useRef<HTMLDivElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const pinnedRef = React.useRef<boolean>(true);
  const [width, setWidth] = React.useState<number>(0);

  useIsoLayoutEffect(function () {
    const el = plotRef.current;
    if (!el) return;
    const measure = function () {
      setWidth(el.clientWidth);
    };
    measure();
    const RO: any = (window as any).ResizeObserver;
    if (RO) {
      const ro = new RO(measure);
      ro.observe(el);
      return function () {
        ro.disconnect();
      };
    }
    window.addEventListener('resize', measure);
    return function () {
      window.removeEventListener('resize', measure);
    };
  }, []);

  /* ---- shape the data ------------------------------------------------ */
  const model = React.useMemo(
    function () {
      const map: { [key: string]: DayGroup } = {};
      const order: string[] = [];
      let max = 0;

      data.forEach(function (d, si) {
        const answers = d.answers || [];
        answers.forEach(function (a) {
          if (!a || typeof a.answer !== 'number') return;
          const dt = new Date(a.date);
          if (isNaN(dt.getTime())) return;
          const y = dt.getFullYear();
          const m = dt.getMonth();
          const day = dt.getDate();
          const key = y + '-' + two(m + 1) + '-' + two(day);
          if (!map[key]) {
            map[key] = {
              key: key,
              ts: new Date(y, m, day).getTime(),
              label: m + 1 + '/' + day,
              full: m + 1 + '/' + day + '/' + y,
              values: {}
            };
            order.push(key);
          }
          map[key].values[si] = a.answer;
          if (a.answer > max) max = a.answer;
        });

        const opts = (d.step && d.step.answers) || [];
        opts.forEach(function (o) {
          if (typeof o.value === 'number' && o.value > max) max = o.value;
        });
      });

      const groups = order
        .map(function (k) {
          return map[k];
        })
        .sort(function (a, b) {
          return a.ts - b.ts;
        });

      return { groups: groups, maxValue: Math.max(max, 1) };
    },
    [data]
  );

  const groups = model.groups;
  const maxValue = model.maxValue;

  /* ---- layout math --------------------------------------------------- */
  const stepCount = Math.max(1, data.length);
  const available = Math.max(0, width - AXIS_WIDTH);
  const groupCount = groups.length;

  const minGroupW = stepCount * MIN_BAR + (stepCount - 1) * BAR_GAP + GROUP_PAD * 2;
  const maxGroupW = stepCount * MAX_BAR + (stepCount - 1) * BAR_GAP + GROUP_PAD * 2;

  let groupW = groupCount > 0 && available > 0 ? available / groupCount : minGroupW;
  groupW = Math.min(Math.max(groupW, minGroupW), maxGroupW);

  const contentW = Math.max(available, groupW * groupCount);
  const offsetX = Math.max(0, (contentW - groupW * groupCount) / 2);
  const barW = Math.max(
    1,
    (groupW - GROUP_PAD * 2 - (stepCount - 1) * BAR_GAP) / stepCount
  );

  const plotH = Math.max(10, height - PAD_TOP - PAD_BOTTOM);
  const baseline = PAD_TOP + plotH;
  const yFor = function (v: number) {
    return PAD_TOP + plotH * (1 - v / maxValue);
  };
  const ticks = makeTicks(maxValue);
  const labelEvery = Math.max(1, Math.ceil(34 / Math.max(groupW, 1)));

  /* ---- keep the latest day in view ----------------------------------- */
  const dataKey = groups
    .map(function (g) {
      return g.key;
    })
    .join('|');

  useIsoLayoutEffect(
    function () {
      pinnedRef.current = true;
      const el = scrollRef.current;
      if (el) el.scrollLeft = el.scrollWidth;
    },
    [dataKey]
  );

  useIsoLayoutEffect(
    function () {
      const el = scrollRef.current;
      if (el && pinnedRef.current) el.scrollLeft = el.scrollWidth;
    },
    [contentW]
  );

  const onScroll = function () {
    const el = scrollRef.current;
    if (!el) return;
    pinnedRef.current = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
  };

  /* ---- render -------------------------------------------------------- */
  const ariaLabel =
    props.ariaLabel ||
    'Daily scores for ' +
      data
        .map(function (d) {
          return d.step ? d.step.shortName : '';
        })
        .join(', ');

  const rootClass = 'sbc-root' + (props.className ? ' ' + props.className : '');

  if (!data.length || !groupCount) {
    return (
      <div className={rootClass}>
        <style>{CSS}</style>
        <div className="sbc-empty">No answers recorded yet.</div>
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <style>{CSS}</style>

      <div className="sbc-plot" ref={plotRef}>
        {/* fixed score axis */}
        <svg
          className="sbc-axis"
          width={AXIS_WIDTH}
          height={height}
          aria-hidden="true"
          focusable="false"
        >
          {ticks.map(function (t) {
            const glyph = EMOJI_SCALE[t];
            const text = glyph || (t === 0 ? '' : String(t));
            if (!text) return null;
            return (
              <text
                key={t}
                className={glyph ? 'sbc-tick sbc-tick-emoji' : 'sbc-tick'}
                x={AXIS_WIDTH - 8}
                y={yFor(t) + (glyph ? 6 : 4)}
                textAnchor="end"
              >
                {text}
                {glyph ? <title>{EMOJI_ALT[t] || String(t)}</title> : null}
              </text>
            );
          })}
          <line
            className="sbc-axisline"
            x1={AXIS_WIDTH - 0.5}
            y1={PAD_TOP}
            x2={AXIS_WIDTH - 0.5}
            y2={baseline}
          />
        </svg>

        {/* scrolling plot */}
        <div className="sbc-scroll" ref={scrollRef} onScroll={onScroll}>
          <svg
            width={contentW}
            height={height}
            role="img"
            aria-label={ariaLabel}
            focusable="false"
          >
            <PatternDefs idPrefix={uid + '-p'} count={data.length} />

            {ticks.map(function (t) {
              return (
                <line
                  key={t}
                  className="sbc-grid"
                  x1={0}
                  y1={yFor(t) + 0.5}
                  x2={contentW}
                  y2={yFor(t) + 0.5}
                />
              );
            })}

            {groups.map(function (g, gi) {
              const gx = offsetX + gi * groupW;
              const showLabel = (groupCount - 1 - gi) % labelEvery === 0;
              return (
                <g key={g.key}>
                  {gi % 2 === 1 ? (
                    <rect
                      className="sbc-band"
                      x={gx}
                      y={PAD_TOP}
                      width={groupW}
                      height={plotH}
                    />
                  ) : null}

                  {data.map(function (d, si) {
                    const v = g.values[si];
                    if (typeof v !== 'number' || v <= 0) return null;
                    const x = gx + GROUP_PAD + si * (barW + BAR_GAP);
                    const top = yFor(v);
                    const h = Math.max(2, baseline - top);
                    return (
                      <rect
                        key={si}
                        className="sbc-bar"
                        x={x}
                        y={top}
                        width={barW}
                        height={h}
                        rx={2}
                        fill={'url(#' + uid + '-p-' + si + ')'}
                        stroke={COLORS[si % COLORS.length]}
                        strokeWidth={1}
                      >
                        <title>
                          {(d.step ? d.step.shortName : 'Step ' + (si + 1)) +
                            ' — ' +
                            g.full +
                            ': ' +
                            labelForValue(d.step, v) +
                            ' (' +
                            v +
                            ')'}
                        </title>
                      </rect>
                    );
                  })}

                  {showLabel ? (
                    <text
                      className="sbc-daylabel"
                      x={gx + groupW / 2}
                      y={baseline + 16}
                      textAnchor="middle"
                    >
                      {g.label}
                    </text>
                  ) : null}
                </g>
              );
            })}

            <line
              className="sbc-axisline"
              x1={0}
              y1={baseline + 0.5}
              x2={contentW}
              y2={baseline + 0.5}
            />
          </svg>
        </div>
      </div>

      {/* key */}
      <ul className="sbc-legend">
        {data.map(function (d, si) {
          const name = d.step ? d.step.shortName : 'Step ' + (si + 1);
          return (
            <li className="sbc-legend-item" key={si}>
              <svg className="sbc-swatch" width={14} height={14} aria-hidden="true">
                <PatternDefs idPrefix={uid + '-l' + si} count={si + 1} />
                <rect
                  x={0.5}
                  y={0.5}
                  width={13}
                  height={13}
                  rx={2}
                  fill={'url(#' + uid + '-l' + si + '-' + si + ')'}
                  stroke={COLORS[si % COLORS.length]}
                  strokeWidth={1}
                />
              </svg>
              {name}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
